import {Button} from '@/components/ui/button/Button'
import {
    DialogDisclosure,
    DialogProvider,
    Dialog,
    DialogBody,
    DialogDismiss,
    DialogFooter
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {PencilLineIcon} from '@/components/ui/icon/Icon'
import {EditProjectDetailsFormProvider} from '@/features/projects/components/project-form/edit-project-details-form-provider/EditProjectDetailsFormProvider'
import {useTranslation} from '@/translations/i18n'
import {ProjectExtended} from '@/features/projects/types'
import {useMemo} from 'react'
import {EditProjectDetailsFormSections} from '@/features/projects/components/project-form/edit-project-details-form-sections/EditProjectDetailsFormSections'
import {EditProjectDetailsFormFooter} from '@/features/projects/components/project-form/edit-project-details-form-footer/EditProjectDetailsFormFooter'
import {useDialogContext} from '@ariakit/react'
import {
    ProjectDetailsDialogHeader,
    ProjectDetailsDialogHeading
} from '@/features/projects/components/project-details-dialog-atoms/ProjectDetailsDialogAtoms'
import dayjs from 'dayjs'

export const ProjectDetailsInfoEditDialog = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    return (
        <DialogProvider>
            <DialogDisclosure
                render={
                    <Button variant="ghost">
                        <PencilLineIcon />
                        {t('commons:edit')}
                    </Button>
                }
            />
            <Dialog width="800px">
                <DialogContent project={project} />
            </Dialog>
        </DialogProvider>
    )
}

const DialogContent = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const dialogContext = useDialogContext()

    const projectToDefaultValues = useMemo(() => {
        // Rule: Avoid unnecessary code duplication
        const formValues = {
            id: project.id,
            projectType: project.type,
            sector: project.sector == 'extended' || project.sector == 'maintenance' ? 'mixed' : project.sector,
            patient: project.patient,
            ...(project.numberOfCycles && {numberOfCycles: project.numberOfCycles}),
            ...(project.assistiveRegime && {assistiveRegime: project.assistiveRegime}),
            ...(project.startDate && {startDate: dayjs(project.startDate).startOf('day').toDate()}),
            ...(project.expirationDate && {expirationDate: dayjs(project.expirationDate).endOf('day').toDate()}),
            ...(project.durationDays && {durationDays: project.durationDays})
        }

        return formValues
    }, [project])

    return (
        <>
            <ProjectDetailsDialogHeader>
                <ProjectDetailsDialogHeading>{t('projects:edit_project_details')}</ProjectDetailsDialogHeading>
                <DialogDismiss />
            </ProjectDetailsDialogHeader>
            <EditProjectDetailsFormProvider defaultValues={projectToDefaultValues}>
                <DialogBody>
                    <EditProjectDetailsFormSections />
                </DialogBody>
                <DialogFooter>
                    <EditProjectDetailsFormFooter onCancel={() => dialogContext?.setOpen(false)} />
                </DialogFooter>
            </EditProjectDetailsFormProvider>
        </>
    )
}
